import { apiCall } from "@/api/client";

/**
 * A measurement preset as the API returns it. The top-level keys are camelCase like everywhere; `rules` and
 * `activationParams` keep their document spelling (snake_case): they are documents edited as JSON and published back
 * as they are, so the editor maps the response to the PUT body explicitly (`presetToBody`) — a GET then a PUT of the
 * same values publishes the same preset.
 */
export type MeasurementPreset = {
  presetId: string;
  version: number;
  name: string;
  description: string;
  rules: Record<string, unknown>;
  exportFormat: "acpm" | "parquet";
  minIpRetentionDays: number;
  activationParams: Record<string, unknown>;
  /** `null` = every tenant. */
  allowedTenants: string[] | null;
  updatedAt: number;
  updatedBy: string;
};

/** A history row: the rules stay server-side (`getMeasurementPresetVersion` reads one version in full). */
export type MeasurementPresetVersion = { version: number; name: string; updatedAt: number; updatedBy: string };

/** What a publication sends (snake_case, the API refuses any other key). `expected_version` 0 creates the preset. */
export type PutMeasurementPresetInput = {
  expected_version: number;
  name: string;
  description: string;
  rules: Record<string, unknown>;
  export_format: MeasurementPreset["exportFormat"];
  min_ip_retention_days: number;
  activation_params: Record<string, unknown>;
  allowed_tenants: string[] | null;
};

/** One refused field of a 400 (`details.fields`), named by its dotted path (`rules.session.gap_ms`). */
export type PresetFieldError = { field: string; message: string };

/** Is this preset offered to that tenant? */
export const presetAllowsTenant = (preset: Pick<MeasurementPreset, "allowedTenants">, tenantId: string) =>
  preset.allowedTenants === null || preset.allowedTenants.includes(tenantId);

// Clés react-query : tout sous ``measurement-presets`` ; la liste est partagée
// avec le formulaire de stage (choix des presets activés).
export const presetKeys = {
  list: ["measurement-presets", "list"] as const,
  detail: (id: string) => ["measurement-presets", "detail", id] as const,
  history: (id: string) => ["measurement-presets", "history", id] as const,
  version: (id: string, version: number) => ["measurement-presets", "version", id, version] as const,
};

const path = (presetId: string) => `/catalog/measurement-presets/${encodeURIComponent(presetId)}`;

export const listMeasurementPresets = (token: string) =>
  apiCall<{ items: MeasurementPreset[] }>("/catalog/measurement-presets", { token });

export const getMeasurementPreset = (token: string, presetId: string) =>
  apiCall<MeasurementPreset>(path(presetId), { token });

export const putMeasurementPreset = (token: string, presetId: string, body: PutMeasurementPresetInput) =>
  apiCall<MeasurementPreset>(path(presetId), { token, method: "PUT", body });

export const getMeasurementPresetHistory = (token: string, presetId: string) =>
  apiCall<{ items: MeasurementPresetVersion[] }>(`${path(presetId)}/history`, { token });

export const getMeasurementPresetVersion = (token: string, presetId: string, version: number) =>
  apiCall<MeasurementPreset>(`${path(presetId)}/history/${version}`, { token });
